import type { typeEnvironments } from '../interfaces';
import { type ClientType, Clients, isChinaEnvironment, ChinaEnvironment } from '../constants';
import { initDatadog } from '@susu/datadog';
import { normalizeEnvironment } from '../utils';

export type CmDataDogConfig = {
  applicationId: string;
  clientToken: string;
  env: typeEnvironments | ChinaEnvironment;
  country: string;
  cmCorrelationId: string;
  client: ClientType;
  version: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  logsSampleRate: number;
  channelId?: string;
  storeID?: string;
};

export const getPlatform = (client: ClientType, country: string, channelId?: string) => {
  if (channelId) {
    return 'shopify';
  }
  if (client === Clients.inStore) {
    return country === 'CN' ? 'buyquickly' : 'predictspring';
  }
  return 'sfcc';
}

export const initializeDataDog = async ({
  applicationId,
  clientToken,
  env,
  country,
  cmCorrelationId,
  client,
  version,
  sessionSampleRate,
  sessionReplaySampleRate,
  logsSampleRate,
  channelId,
  storeID,
}: CmDataDogConfig) => {
  if (!window) {
    console.warn('Datadog is not available in server environment')
    return;
  }

  if (!env || env === 'dev') {
    console.warn('Datadog is disabled in development environment')
    return;
  }

  if (window.location.hostname === 'localhost') {
    console.warn('Datadog is disabled on localhost')
    return;
  }

  if (!applicationId || !clientToken) {
    console.warn('Datadog credentials missing, skipping initialization')
    return
  }

  if (!version) {
    console.warn('Datadog APP_VERSION missing, skipping initialization')
    return;
  }

  const service = isChinaEnvironment(env) ? 'custom-made-configurator-cn-v2' : 'custom-made-configurator-v2';

  const { cleanup } = await initDatadog({
    service,
    env: normalizeEnvironment(env),
    version,
    platform: getPlatform(client, country, channelId),
    country,
    locationId: storeID ? Number(storeID) : undefined,
    applicationId,
    clientToken,
    sessionSampleRate,
    sessionReplaySampleRate,
    logsSampleRate,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    forwardErrorsToLogs: true,
    additionalTags: {
      cmCorrelationId,
    },
  });

  return cleanup;
};
